import { useState, useEffect } from 'react';
import { Button } from '@chakra-ui/react';
import { APICall, CourseItemsDisabled, CourseMandatory as Mandatory, CourseVisible as Visible } from '../../../utilities/types';
import { ICourse, InputFieldConfig, IRegionConfig, ScholarshipSponsorship, ScholarshipSponsorshipMap } from 'types';
import { getDateFromString, getStringFromDate } from '../../../utilities/helpers';
import { API_CALLS } from '../../../utilities/constants';
import { IDate, IQuestion, Q } from '../../../types';
import { isValidDate as isValidDateHelper } from '../../../utilities/validation';
import { calculateFeeAfterScholarships } from '../../../utilities/course';
import { slug } from '../../../utilities/formatting';

import { AutocompleteInputWithConfirm } from '../../formInputs/AutocompleteInput';
import { DateInputWithConfirm } from '../../formInputs/DateInput';
import { InputWithConfirm } from '../../formInputs/Input';
import { RadioOptionsWithConfirm } from '../../formInputs/RadioOptions';
import { CurrencyInputWithConfirm } from '../../formInputs/CurrencyInput';

import { ContinueButton } from '../../atoms/ContinueButton';

export interface IProps {
  index: number;
  level?: string;
  levelOther?: string;
  courseName?: string;
  courseFee?: number;
  cah3?: string;
  startDate?: string;
  endDate?: string;
  isWorkPlacement?: boolean;
  isAtas?: boolean;
  innerLondon?: boolean;
  lastAcceptanceDate?: string;
  suggestions: ICourse[];
  config: IRegionConfig;
  visible: Visible;
  itemsDisabled: CourseItemsDisabled;
  mandatory: Mandatory;
  apiCallsInProgress: APICall[];
  canUseAutoSuggest?: boolean;
  courseScholarships: ScholarshipSponsorshipMap;
  isScholarshipDisabled?: boolean;
  onUpdateScholarship: (data: { key: string; data: ScholarshipSponsorship }[]) => void;
  getCourseSuggestions: (query: string) => void;
  clearSuggestions: () => void;
  onSaveAndClose: (course: Partial<ICourse>) => void;
}

export const Course = (props: IProps) => {
  const {
    index,
    apiCallsInProgress,
    visible,
    mandatory,
    onUpdateScholarship,
    getCourseSuggestions,
    clearSuggestions,
    canUseAutoSuggest = false,
    onSaveAndClose,
    itemsDisabled
  } = props;

  const [courseName, setCourseName] = useState(props.courseName);
  const [courseFee, setCourseFee] = useState<number | undefined>(props.courseFee);

  const [courseScholarships, setCourseScholarships] = useState<ScholarshipSponsorshipMap>(props.courseScholarships);

  const [isWorkPlacement, setIsWorkPlacement] = useState(props.isWorkPlacement);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true); // default to loading first
  const [courseNotListedOrResearchStudent, setCourseNotListedOrResearchStudent] = useState(false);
  const [sDate, setSDate] = useState(() => getDateFromString(props.startDate));
  const [eDate, setEDate] = useState(() => getDateFromString(props.endDate));
  const [sDateErrorMsg, setSDateErrorMsg] = useState('');
  const [eDateErrorMsg, setEDateErrorMsg] = useState('');

  const [complete, setComplete] = useState(
    (!visible.manualCourseName || !mandatory.manualCourseName || courseName !== '') &&
      (!visible.startDate || !mandatory.startDate || getStringFromDate(sDate) !== '') &&
      (!visible.endDate || !mandatory.endDate || getStringFromDate(eDate) !== '') &&
      (!visible.courseFee || !mandatory.courseFee || courseFee !== undefined) &&
      (!visible.isWorkPlacement || !mandatory.isWorkPlacement || isWorkPlacement !== undefined)
  );

  const [suggestions, setSuggestions] = useState<ICourse[]>(props.suggestions);
  const {
    AutoCourseName: autoCourseNameConfig,
    ManualCourseName: manualCourseNameConfig,
    CourseFee: courseFeeConfig,
    StartDate: startDateConfig,
    EndDate: endDateConfig,
    IsWorkPlacement: isWorkPlacementConfig,
    CourseScholarshipAmount1: courseScholarshipAmount1Config
  }: {
    AutoCourseName: InputFieldConfig;
    ManualCourseName: InputFieldConfig;
    CourseFee: InputFieldConfig;
    StartDate: InputFieldConfig;
    EndDate: InputFieldConfig;
    IsWorkPlacement: InputFieldConfig;
    CourseScholarshipAmount1: InputFieldConfig;
  } = props.config.Checklist.PersonalInfo.Page5;

  useEffect(() => {
    const isLoading = apiCallsInProgress.find(x => x.callName === API_CALLS.GET_COURSES) !== undefined;
    if (isLoadingSuggestions !== isLoading) {
      setIsLoadingSuggestions(isLoading);
    }
  }, [apiCallsInProgress, isLoadingSuggestions]);

  useEffect(
    () => {
      if (JSON.stringify(suggestions) !== JSON.stringify(props.suggestions)) {
        setSuggestions(props.suggestions);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.suggestions]
  );

  const hasNoCourseScholarships = Object.keys(props.courseScholarships).length === 0;

  const saveAndClose = () => {
    const bulkUpdateData = Object.keys(courseScholarships)
      .filter(key => courseScholarships[key]?.courseFundAmount !== props.courseScholarships[key]?.courseFundAmount)
      .map(key => {
        return {
          key,
          data: courseScholarships[key]
        };
      });
    if (bulkUpdateData.length !== 0) onUpdateScholarship(bulkUpdateData);

    onSaveAndClose({
      courseLevel: props.level,
      courseLevelOther: props.levelOther,
      courseName: courseName ?? '',
      startDate: getStringFromDate(sDate),
      endDate: getStringFromDate(eDate),
      cah3: props.cah3,
      courseFee: courseFee ?? 0,
      isWorkPlacement: isWorkPlacement ?? false,
      isAtas: props.isAtas,
      innerLondon: props.innerLondon,
      lastAcceptanceDate: props.lastAcceptanceDate
    });
  };

  const handleOnSaveAndCloseClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    saveAndClose();
  };

  const isValidDate = (date: IDate): { isValid: boolean; message?: string } => {
    const { YYYY, DD, MM } = date;
    if (YYYY && DD && MM) {
      const isValid = isValidDateHelper(date);
      if (!isValid) {
        return { isValid: false, message: "Doesn't look like a valid date" };
      } else {
        return { isValid: true };
      }
    } else {
      return { isValid: false, message: '' };
    }
  };

  const handleStartDateChange = (date?: IDate) => {
    const { isValid, message } = isValidDate(date || sDate);
    if (isValid && sDateErrorMsg !== '') {
      setSDateErrorMsg('');
    } else {
      setSDateErrorMsg(message || '');
    }
  };

  const handleEndDateChange = (date?: IDate) => {
    const { isValid, message } = isValidDate(date || eDate);
    if (isValid && eDateErrorMsg !== '') {
      setEDateErrorMsg('');
    } else {
      setEDateErrorMsg(message || '');
    }
  };

  const handleSuggestionChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    setCourseName(value);
    setIsLoadingSuggestions(true);
    getCourseSuggestions(value);
  };

  const onCourseBlur = (event: React.FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value || '';
    const course: ICourse | undefined = suggestions.find(x => slug(x.courseName) === slug(value));
    if (suggestions.length && course !== undefined) {
      handleCourseUpdate(course);
    }
  };

  const handleCourseUpdate = (course: ICourse) => {
    if (course) {
      if (course.startDate !== '') {
        setSDate(getDateFromString(course.startDate));
      }
    }
    clearSuggestions();
  };

  useEffect(() => {
    setComplete(
      (!visible.manualCourseName || !mandatory.manualCourseName || courseName !== '') &&
        (!visible.startDate || !mandatory.startDate || getStringFromDate(sDate) !== '') &&
        (!visible.endDate || !mandatory.endDate || getStringFromDate(eDate) !== '') &&
        (!visible.courseFee || !mandatory.courseFee || (courseFee !== undefined && courseFee !== null)) &&
        (!visible.isWorkPlacement || !mandatory.isWorkPlacement || isWorkPlacement !== undefined) &&
        sDateErrorMsg === '' &&
        eDateErrorMsg === ''
    );
  }, [courseName, courseFee, isWorkPlacement, sDate, eDate, sDateErrorMsg, eDateErrorMsg, mandatory, visible]);

  const clearCourse = () => {
    setSDate({ DD: null, MM: null, YYYY: null });
    setEDate({ DD: null, MM: null, YYYY: null });
    setCourseFee(undefined);
    setIsWorkPlacement(false);
  };

  return (
    <>
      {canUseAutoSuggest && (
        <>
          {courseNotListedOrResearchStudent ? (
            <Button
              id="courseNotFound"
              onClick={() => {
                clearCourse();
                setCourseNotListedOrResearchStudent(false);
              }}
            >
              Auto-suggest
            </Button>
          ) : (
            <Button
              id="courseNotFound"
              onClick={() => {
                clearCourse();
                setCourseNotListedOrResearchStudent(true);
              }}
            >
              Course not listed / I am a research student
            </Button>
          )}
        </>
      )}

      {visible.manualCourseName && (
        <InputWithConfirm
          key={Q.COURSE_NAME}
          id={Q.COURSE_NAME}
          disabled={itemsDisabled?.manualCourseName}
          autoFocus={true}
          value={courseName}
          label={manualCourseNameConfig?.label}
          onChange={(evt: any) => {
            setCourseName(evt.currentTarget.value);
          }}
        />
      )}

      {visible.autoCourseName && canUseAutoSuggest && (
        <AutocompleteInputWithConfirm
          key={Q.COURSE_NAME}
          id={Q.COURSE_NAME}
          disabled={itemsDisabled?.autoCourseName}
          value={courseName}
          label={autoCourseNameConfig?.label}
          inputHelperText={autoCourseNameConfig?.helpText}
          onChange={(evt: any) => handleSuggestionChange(evt)}
          onBlur={onCourseBlur}
          suggestions={suggestions.map(x => x.courseName)}
          suggestionsLoading={isLoadingSuggestions}
        />
      )}

      {visible.courseFee && (
        <CurrencyInputWithConfirm
          key={`${Q.COURSE_FEE}`}
          id={Q.COURSE_FEE}
          disabled={itemsDisabled?.courseFee}
          value={courseFee}
          label={courseFeeConfig?.label}
          subText={courseFeeConfig?.subLabel}
          inputHelperText={courseFeeConfig?.helpText}
          onChange={(evt: any) => {
            setCourseFee(evt.currentTarget.value === '' ? undefined : Number(evt.currentTarget.value));
          }}
        />
      )}

      {hasNoCourseScholarships
        ? !props.isScholarshipDisabled && (
            <CurrencyInputWithConfirm
              key={`${Q.COURSE_SCHOLARSHIP_AMOUNT1}`}
              id={Q.COURSE_SCHOLARSHIP_AMOUNT1}
              value={courseScholarships[`courseScholarshipAmount1_course${index}`]?.courseFundAmount || 0}
              label={'Scholarship'}
              subText={courseScholarshipAmount1Config?.subLabel}
              inputHelperText={courseScholarshipAmount1Config?.helpText}
              onChange={(evt: any) => {
                setCourseScholarships({
                  ...courseScholarships,
                  [`courseScholarshipAmount1_course${index}`]: {
                    scholarshipName: 'Scholarship',
                    sponsorshipCovers: [courseName ?? ''],
                    courseFundType: 'Amount',
                    courseFundPercentage: 0,
                    livingFundType: '',
                    livingFundPercentage: 0,
                    livingFundAmountType: '',
                    livingFundAmount: 0,
                    courseAndLivingFundType: '',
                    courseAndLivingFundPercentage: 0,
                    courseAndLivingFundAmount: 0,
                    courseFundAmount: Number(evt.currentTarget.value)
                  }
                });
              }}
            />
          )
        : Object.keys(courseScholarships).map(key => (
            <CurrencyInputWithConfirm
              key={key}
              id={key}
              value={courseScholarships[key].courseFundAmount}
              label={courseScholarships[key].scholarshipName}
              subText={!props.isScholarshipDisabled && courseScholarshipAmount1Config?.subLabel}
              inputHelperText={!props.isScholarshipDisabled && courseScholarshipAmount1Config?.helpText}
              onChange={(evt: any) => {
                setCourseScholarships({
                  ...courseScholarships,
                  [key]: {
                    ...courseScholarships[key],
                    courseFundAmount: Number(evt.currentTarget.value)
                  }
                });
              }}
              disabled={props.isScholarshipDisabled}
            />
          ))}

      {!(hasNoCourseScholarships && props.isScholarshipDisabled) && (
        <CurrencyInputWithConfirm
          key={`${Q.FEE_AFTER_SCHOLARSHIP}`}
          id={Q.FEE_AFTER_SCHOLARSHIP}
          value={calculateFeeAfterScholarships(courseFee, courseScholarships)}
          label={'Fee After Scholarship(s)'}
          inputHelperText={`Calculated based on the first-year course fee and any discounts or scholarships above.`}
          disabled
        />
      )}

      {visible.startDate && (
        <DateInputWithConfirm
          key={Q.START_DATE}
          id={Q.START_DATE}
          disabled={itemsDisabled?.startDate}
          label={startDateConfig?.label}
          value={sDate}
          inputHelperText={startDateConfig?.helpText}
          errorText={sDateErrorMsg}
          onChangeDD={(evt: any) => setSDate({ ...sDate, DD: evt.target.value })}
          onChangeMM={(evt: any) => setSDate({ ...sDate, MM: evt.target.value })}
          onChangeYY={(evt: any) => setSDate({ ...sDate, YYYY: evt.target.value })}
          onBlur={() => handleStartDateChange()}
        />
      )}

      {visible.endDate && (
        <DateInputWithConfirm
          key={Q.END_DATE}
          id={Q.END_DATE}
          disabled={itemsDisabled?.endDate}
          label={endDateConfig?.label}
          value={eDate}
          inputHelperText={endDateConfig?.helpText}
          errorText={eDateErrorMsg}
          onChangeDD={(evt: any) => setEDate({ ...eDate, DD: evt.target.value })}
          onChangeMM={(evt: any) => setEDate({ ...eDate, MM: evt.target.value })}
          onChangeYY={(evt: any) => setEDate({ ...eDate, YYYY: evt.target.value })}
          onBlur={() => handleEndDateChange()}
        />
      )}

      {visible.isWorkPlacement && (
        <RadioOptionsWithConfirm
          key={Q.HAS_WORK_PLACEMENT}
          id={Q.HAS_WORK_PLACEMENT}
          disabled={itemsDisabled?.isWorkPlacement}
          label={isWorkPlacementConfig?.label}
          inputHelperText={isWorkPlacementConfig?.helpText}
          isInline={true}
          onClick={(question: IQuestion) => setIsWorkPlacement(question.label === 'Yes')}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: isWorkPlacement === true
            },
            {
              char: 'N',
              label: 'No',
              selected: isWorkPlacement === false
            }
          ]}
        />
      )}

      <ContinueButton index={index} complete={complete} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} />
    </>
  );
};
